import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Utensils, Heart, Globe, Users, Lightbulb } from 'lucide-react';
import './ServicesBannerCarousel.css';

interface ServiceSlide {
  id: number;
  title: string;
  subtitle: string;
  points: Array<{
    title: string;
    description: string;
    icon: React.ReactNode;
  }>;
  backgroundGradient: string;
}

const ServicesBannerCarousel: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const slides: ServiceSlide[] = [
    {
      id: 1,
      title: "Build Confidence & Reduce Stress",
      subtitle: "Empowering Your Culinary Journey",
      points: [
        {
          title: "Cooking Confidence",
          description: "Feel empowered as recipes break down complex techniques into easy, manageable steps that boost kitchen self-assurance.",
          icon: <Utensils size={40} />
        },
        {
          title: "Stress-Free Guidance", 
          description: "Helpful support and encouragement reduce the intimidation of cooking, making the whole process more enjoyable.",
          icon: <Heart size={40} />
        }
      ],
      backgroundGradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    },
    {
      id: 2,
      title: "Expand Your Culinary Horizons",
      subtitle: "Variety & Skill Development",
      points: [
        {
          title: "Increased Variety",
          description: "Explore different cuisines—from Thai curries to Mediterranean fare—keeping meals exciting and diverse.",
          icon: <Globe size={40} />
        },
        {
          title: "Skill Building",
          description: "Learn practical kitchen know-how—knife skills, sauces, troubleshooting—which builds culinary competence and independence.",
          icon: <Lightbulb size={40} />
        }
      ],
      backgroundGradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
    },
    {
      id: 3,
      title: "Quality & Health Focus",
      subtitle: "Fresh Ingredients & Balanced Nutrition",
      points: [
        {
          title: "Seasonal Quality",
          description: "Emphasis on fresh, seasonal ingredients helps you cook with peak flavors and nutritional benefit, elevating overall meal quality.",
          icon: <Utensils size={40} />
        },
        {
          title: "Balanced Eating",
          description: "By prioritizing whole foods, lean proteins, and fresh produce, you'll naturally create healthier dishes without feeling deprived.",
          icon: <Heart size={40} />
        }
      ],
      backgroundGradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
    },
    {
      id: 4,
      title: "Creative Expression & Culture",
      subtitle: "Personalization & Global Exploration",
      points: [
        {
          title: "Creative Outlet",
          description: "Encouraging you to customize and personalize meals allows you to express creativity and find joy in the process.",
          icon: <Lightbulb size={40} />
        },
        {
          title: "Cultural Exploration",
          description: "Global flavors adapted for everyday cooks lets you experience world cuisines and broaden your culinary horizons.",
          icon: <Globe size={40} />
        }
      ],
      backgroundGradient: "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)"
    },
    {
      id: 5,
      title: "Connection & Growth",
      subtitle: "Building Relationships Through Food",
      points: [
        {
          title: "Connection Through Cooking",
          description: "Highlights cooking as a means of love and family tradition—helping you connect with loved ones when you cook and share together.",
          icon: <Users size={40} />
        },
        {
          title: "Motivation to Try More",
          description: "With its upbeat tone and inclusive approach, the page encourages you to experiment—even tackling recipes that challenge you.",
          icon: <Heart size={40} />
        }
      ],
      backgroundGradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    }
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, slides.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  return (
    <div className="services-banner-carousel">
      <div className="services-carousel-header">
        <h2 className="services-carousel-title">
          Discover 10 Life-Changing Benefits from HomeCookt's About Page
        </h2>
        <p className="services-carousel-subtitle">
          Transform your cooking journey with these powerful advantages — beyond just website features
        </p>
        <a 
          href="https://homecookt.com/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="homecookt-button"
        >
          Go to HomeCookt.com
        </a>
      </div>
      <div className="services-carousel-container">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`services-carousel-slide ${index === currentSlide ? 'active' : ''}`}
            style={{ background: slide.backgroundGradient }}
          >
            <div className="services-slide-content">
              <div className="services-slide-header">
                <h1 className="services-slide-title">{slide.title}</h1>
                <h2 className="services-slide-subtitle">{slide.subtitle}</h2>
              </div>
              
              <div className="services-slide-points">
                {slide.points.map((point, pointIndex) => (
                  <div key={pointIndex} className="services-point-card">
                    <div className="services-point-icon">
                      {point.icon}
                    </div>
                    <div className="services-point-content">
                      <h3 className="services-point-title">{point.title}</h3>
                      <p className="services-point-description">{point.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}

        {/* Navigation Arrows */}
        <button className="services-carousel-nav prev" onClick={goToPrevious}>
          <ChevronLeft size={24} />
        </button>
        <button className="services-carousel-nav next" onClick={goToNext}>
          <ChevronRight size={24} />
        </button>

        {/* Dots Indicator */}
        <div className="services-carousel-dots">
          {slides.map((_, index) => (
            <button
              key={index}
              className={`services-dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ServicesBannerCarousel;